import type {unitOfTime} from 'moment';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Text, Icon} from '@albert-io/atomic';

import LiveRegion from 'lib/LiveRegion';

import {
  lastSaved,
  saved,
  useQuestionTranslationStore,
  draftGuess
} from 'client/components/QuestionTypes/shared/TranslationSection';

import QuestionTypeStore from '../../components/QuestionTypes/QuestionType.store';
import {SaveStatus} from '../../components/QuestionTypes/QuestionType.types';

interface StoreWithDraftGuessCapabilities {
  getSaveStatus: () => SaveStatus;
  getLastSaveTime: () => Date | null;
}

interface Props {
  store: StoreWithDraftGuessCapabilities;
}

/** The draft's save status, shown only where the draft is actually being saved. */
export default function DraftGuessSaveStatus({store}: Props) {
  const [now, setNow] = useState(() => new Date());
  const status = store.getSaveStatus();
  const lastSaveTime = store.getLastSaveTime();
  const [offline, setOffline] = useState(false);

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date()); // refresh the relative timestamp each minute
    }, 60000);

    return () => {
      clearInterval(id);
    };
  }, [setNow]);

  useEffect(() => {
    const handleOffline = () => {
      setOffline(true);
    };

    const handleOnline = () => {
      setOffline(false);
    };

    global.addEventListener('offline', handleOffline);
    global.addEventListener('online', handleOnline);

    return () => {
      global.removeEventListener('offline', handleOffline);
      global.removeEventListener('online', handleOnline);
    };
  }, [setOffline]);

  function isError() {
    return [SaveStatus.LOAD_ERROR, SaveStatus.SAVE_ERROR].includes(status);
  }

  function isOfflineOrError() {
    return offline || isError();
  }

  function getIconProps() {
    let className;
    let color;
    let emphasis;
    let icon;

    if (isOfflineOrError()) {
      color = 'negative';
      emphasis = 'lowest';
      icon = ['fas', 'exclamation-triangle'];
    } else {
      className = 'content-tertiary';
      icon = ['far'];

      if (status === SaveStatus.SAVING) {
        icon.push('sync');
      } else if (status === SaveStatus.SAVED && !!lastSaveTime) {
        icon.push('check');
      } else {
        icon.push('cloud-upload');
      }
    }

    return {
      className,
      color,
      emphasis,
      icon
    };
  }

  function getTextProps() {
    const className = ['ml-x1'];
    let color;
    let emphasis;
    let size;

    if (isOfflineOrError()) {
      color = 'red';
      emphasis = 'high';
    } else {
      className.push('content-tertiary');
    }

    if (!isError()) {
      size = 's';
    }

    return {
      className: className.join(' '),
      color,
      emphasis,
      size
    };
  }

  function getUserFriendlyTimeDiff(earlierDate: Date, laterDate: Date) {
    const earlierDateUtc = moment.utc(earlierDate);
    const laterDateUtc = moment.utc(laterDate);

    const diffUnits: unitOfTime.Diff[] = ['days', 'hours', 'minutes'];

    for (let i = 0; i < diffUnits.length; i += 1) {
      const diffUnit = diffUnits[i];
      const diff = laterDateUtc.diff(earlierDateUtc, diffUnit);

      if (diff > 0) {
        const isPlural = diff !== 1;
        if (showTranslations) {
          return `La última edición fue hace ${diff} ${
            isPlural ? draftGuess.pluralTimeUnits[i] : draftGuess.timeUnits[i]
          }`;
        }
        return `Last edit was ${diff} ${isPlural ? diffUnit : diffUnit.slice(0, -1)} ago`;
      }
    }

    return showTranslations ? lastSaved : 'Last edit was just now';
  }

  /**
   * Splits the status line into the part worth announcing and the trailing detail that is not.
   * A 60s interval rerenders the relative timestamp, so a live region covering it would repeat
   * "Saved, last edit was N minutes ago" every minute while the student types. The detail stays
   * readable — it is simply outside the live region.
   */
  function getStatusParts(): {announce: string; detail: string} {
    if (offline) {
      const offlineText = showTranslations ? draftGuess.offline : 'No network connection.';
      return {
        announce: offlineText,
        detail: lastSaveTime ? ` Last saved ${lastSaveTime.toLocaleString()}` : ''
      };
    }

    if (status === SaveStatus.SAVE_ERROR) {
      return {
        announce: showTranslations ? draftGuess.saveError : 'There was an error saving your draft.',
        detail: ''
      };
    }

    if (status === SaveStatus.LOAD_ERROR) {
      return {
        announce: showTranslations
          ? draftGuess.loadError
          : 'There was an error loading your draft.',
        detail: ''
      };
    }

    if (status === SaveStatus.SAVING) {
      return {announce: showTranslations ? draftGuess.saving : 'Saving', detail: ''};
    }

    if (status === SaveStatus.SAVED && lastSaveTime) {
      const timeDiff = getUserFriendlyTimeDiff(lastSaveTime, now);
      return {announce: showTranslations ? saved : 'Saved', detail: `, ${timeDiff}`};
    }

    return {
      announce: showTranslations ? draftGuess.default : 'Your work will be autosaved',
      detail: ''
    };
  }

  if (QuestionTypeStore.shouldNotSaveDraft()) {
    return null;
  }

  const {announce, detail} = getStatusParts();

  return (
    <>
      <Icon {...getIconProps()} />
      <Text {...getTextProps()}>
        <LiveRegion assertive={isOfflineOrError()}>{announce}</LiveRegion>
        {detail}
      </Text>
    </>
  );
}
